import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe, Briefcase, Home, Wrench, Image, Info, Phone, MessageCircle, ChevronRight, Sparkles } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useRegion } from '../context/RegionContext';
import { translations } from '../translations/translations';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { language, changeLanguage } = useLanguage();
  const { getLink, region, basePath } = useRegion();
  const t = translations[language];
  const location = useLocation();

  // Close menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  const isActive = (path) => {
    const fullPath = path === '/' ? basePath : `${basePath}${path}`;
    return location.pathname === fullPath;
  };

  const menuItems = [
    { path: '/', label: t.nav.home, icon: Home },
    { path: '/gallery', label: t.nav.gallery, icon: Image },
    { path: '/about', label: t.nav.about, icon: Info },
  ];

  return (
    <>
      <header className="bg-[#1a5c3a] shadow-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-3 md:py-4">
            {/* Logo */}
            <Link 
              to={getLink('/')} 
              className="flex items-center gap-2" 
              data-testid="logo-link"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_trusted-maintenance-1/artifacts/l9fyih4f_Whisk_4dd066d44f7c3e88b4a47b0cc96820dddr-removebg-preview.png" 
                alt="Sancur Services" 
                className="h-12 md:h-14 w-auto brightness-0 invert"
              />
              <span className="text-[#d4a84b] font-heading text-xl md:text-2xl font-bold tracking-tight">
                Sancur Services
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              {menuItems.map((item) => (
                <Link
                  key={item.path}
                  to={getLink(item.path)}
                  data-testid={`nav-${item.path.replace('/', '') || 'home'}`}
                  className={`font-body transition-colors px-1 ${
                    isActive(item.path) ? 'text-white font-semibold' : 'text-white/80 hover:text-white'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
              <div className="w-px h-6 bg-white/20" /> {/* Divider */}
              <Link
                to="/staff/login"
                data-testid="nav-staff-area"
                className="font-body transition-colors flex items-center gap-2 text-white/80 hover:text-white"
              >
                <Briefcase size={18} />
                {language === 'pt' ? 'Área Staff' : 'Staff Area'}
              </Link>
              <Link
                to="/ie/quote"
                data-testid="nav-contact"
                className="font-body px-5 py-2 bg-white text-[#1a5c3a] border-2 border-white rounded-full font-semibold hover:bg-[#d4af37] hover:border-[#d4af37] hover:text-white transition-all ml-2"
              >
                {t.nav.contact}
              </Link>
              
              <div className="w-px h-6 bg-white/20" /> {/* Divider */}
              
              {/* Language Toggle */}
              <div className="flex items-center gap-1 font-body text-sm" data-testid="language-toggle-desktop">
                <button
                  onClick={() => changeLanguage('pt')}
                  className={`px-2 py-1 rounded transition-colors ${language === 'pt' ? 'bg-white/20 text-white font-bold' : 'text-white/70 hover:text-white'}`}
                >
                  PT
                </button>
                <span className="text-white/50">|</span>
                <button
                  onClick={() => changeLanguage('en')}
                  className={`px-2 py-1 rounded transition-colors ${language === 'en' ? 'bg-white/20 text-white font-bold' : 'text-white/70 hover:text-white'}`}
                >
                  EN
                </button>
              </div>
            </nav>

            {/* Mobile: CTA + Menu Toggle */}
            <div className="flex items-center gap-2 md:hidden">
              <Link
                to="/ie/quote"
                className="px-3 py-1.5 bg-[#d4af37] text-white text-sm font-semibold rounded-full flex items-center gap-1"
              >
                <Sparkles size={14} />
                <span>{language === 'pt' ? 'Orçamento' : 'Quote'}</span>
              </Link>
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="p-2 text-white rounded-lg hover:bg-white/10 transition-colors"
                data-testid="mobile-menu-toggle"
              >
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div 
        className={`fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 md:hidden ${
          isMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setIsMenuOpen(false)}
      />

      {/* Mobile Menu Drawer */}
      <div 
        className={`fixed top-0 right-0 h-full w-[280px] bg-[#1a5c3a] z-50 transform transition-transform duration-300 ease-out md:hidden ${
          isMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        data-testid="mobile-menu"
      >
        {/* Menu Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <button 
            onClick={() => {
              setIsMenuOpen(false);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className="flex items-center gap-2"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_trusted-maintenance-1/artifacts/l9fyih4f_Whisk_4dd066d44f7c3e88b4a47b0cc96820dddr-removebg-preview.png" 
              alt="Sancur Services" 
              className="h-10 brightness-0 invert"
            />
            <span className="text-[#d4a84b] font-heading text-lg font-bold tracking-tight">
              Sancur Services
            </span>
          </button>
          <button
            onClick={() => setIsMenuOpen(false)}
            className="p-2 text-white/70 hover:text-white rounded-lg hover:bg-white/10"
          >
            <X size={24} />
          </button>
        </div>

        {/* Menu Items */}
        <div className="p-4 space-y-1">
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={getLink(item.path)}
                onClick={() => setIsMenuOpen(false)}
                data-testid={`mobile-nav-${item.path.replace('/', '') || 'home'}`}
                className={`flex items-center justify-between p-3 rounded-xl transition-all ${
                  isActive(item.path) 
                    ? 'bg-white/20 text-white' 
                    : 'text-white/80 hover:bg-white/10 hover:text-white'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Icon size={20} />
                  <span className="font-medium">{item.label}</span>
                </div>
                <ChevronRight size={18} className="text-white/50" />
              </Link>
            );
          })}

          {/* Divider */}
          <div className="my-4 border-t border-white/10" />

          {/* Staff Area */}
          <Link
            to="/staff/login"
            onClick={() => setIsMenuOpen(false)}
            data-testid="mobile-nav-staff-area"
            className="flex items-center justify-between p-3 rounded-xl text-white/80 hover:bg-white/10 hover:text-white transition-all"
          >
            <div className="flex items-center gap-3">
              <Briefcase size={20} />
              <span className="font-medium">{language === 'pt' ? 'Área Staff' : 'Staff Area'}</span>
            </div>
            <ChevronRight size={18} className="text-white/50" />
          </Link>

          {/* Get Quote CTA */}
          <Link
            to="/ie/quote"
            onClick={() => setIsMenuOpen(false)}
            data-testid="mobile-nav-contact"
            className="flex items-center justify-center gap-2 p-4 mt-4 bg-[#d4af37] text-white rounded-xl font-semibold hover:bg-[#c4a030] transition-all"
          >
            <Sparkles size={20} />
            <span>{language === 'pt' ? 'Pedir Orçamento Grátis' : 'Get Free Quote'}</span>
          </Link>
        </div>

        {/* Bottom Section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-[#0f3d26]">
          {/* Quick Contact */}
          <div className="flex gap-2 mb-4">
            <a 
              href="tel:+353899502499"
              className="flex-1 flex items-center justify-center gap-2 p-3 bg-white/10 rounded-xl text-white text-sm hover:bg-white/20 transition-all"
            >
              <Phone size={16} />
              <span>{language === 'pt' ? 'Ligar' : 'Call'}</span>
            </a>
            <a 
              href="https://wa.me/353899502499"
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center gap-2 p-3 bg-[#25D366] rounded-xl text-white text-sm hover:bg-[#20BA5A] transition-all"
            >
              <MessageCircle size={16} />
              <span>WhatsApp</span>
            </a>
          </div>

          {/* Language Toggle */}
          <div className="flex items-center justify-center gap-2" data-testid="language-toggle-mobile">
            <span className="text-white/50 text-xs mr-2">
              <Globe size={14} className="inline mr-1" />
              {language === 'pt' ? 'Idioma:' : 'Language:'}
            </span>
            <button
              onClick={() => changeLanguage('pt')}
              className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                language === 'pt' ? 'bg-white text-[#1a5c3a] font-bold' : 'text-white/70 hover:bg-white/10'
              }`}
            >
              PT
            </button>
            <button
              onClick={() => changeLanguage('en')}
              className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                language === 'en' ? 'bg-white text-[#1a5c3a] font-bold' : 'text-white/70 hover:bg-white/10'
              }`}
            >
              EN
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
